import React, { useRef, useState } from "react";
// 导入 React-Bootstrap 组件，用于构建表单、按钮、卡片和错误提示
import { Form, Button, Card, Alert } from "react-bootstrap";
// 导入自定义认证上下文 Hook，用于调用注册方法
import { useAuth } from "../../contexts/AuthContext";
// 导入 React Router 组件，用于页面导航和链接
import { Link, useNavigate } from "react-router-dom";
// 导入 Bootstrap 样式
import "bootstrap/dist/css/bootstrap.min.css";
import "./Form.css";
import { sendEmailVerification } from "firebase/auth";

/*
  Signup 组件用于处理用户注册功能：
  - 用户输入邮箱、密码和密码确认。
  - 提交表单时，会调用 signup 方法创建新账户。
  - 根据操作状态（loading、error）反馈用户信息。
*/
const Signup = () => {
  // useRef 用于引用输入框 DOM 元素，以便获取用户输入的值
  const emailRef = useRef();
  const passwordRef = useRef();
  const passwordConfirmRef = useRef();

  // 从认证上下文中提取 signup 方法
  const { signup, logout, signUpWithGoogle, signUpWithFacebook, signUpWithMicrosoft } = useAuth();
        // 当前登陆的用户：currentUser，可用作判断用户是否登陆。要求其注册登陆
  // 定义 error 状态，用于存储错误提示信息
  const [error, setError] = useState("");
  // 定义 loading 状态，用于标记注册操作是否正在进行
  const [loading, setLoading] = useState(false);

  // useNavigate 用于在注册成功后跳转到首页
  const navigate = useNavigate();

  // 处理表单提交的异步函数
  const handleSubmit = async (e) => {
    // 阻止表单默认提交行为
    e.preventDefault();

    // 如果密码和确认密码不一致，则显示错误信息并终止注册操作
    if (passwordRef.current.value !== passwordConfirmRef.current.value) {
      return setError("Passwords do not match");
    }

    try {
      // 重置错误信息并开始加载
      setError("");
      setLoading(true);
      // 调用 signup 方法创建新用户，传入邮箱和密码
      const userCredential = await signup(emailRef.current.value, passwordRef.current.value);
    
      // 注意：在 AuthContext 中signup方法已不包含sendEmailVerification，
      // 或者你可以在这里单独调用：
      await sendEmailVerification(userCredential.user);
      // 注销当前用户，避免自动保持登录状态
      await logout();
    // 注册成功后跳转
      alert("Registration successful! A verification email has been sent. Please verify your email before logging in.");
      navigate("/login");
    } catch (err) {
      // 捕获错误并在控制台打印，同时更新错误状态以提示用户
      console.error(err);
      setError("Failed to create an account");
    } finally {
      // 操作结束后重置加载状态
      setLoading(false);
    }
  };
 // Google 注册
 const handleGoogleSignup = async () => {
  try {
    setError("");
    setLoading(true);
    await signUpWithGoogle();
    navigate("/");
  } catch (err) {
    console.error(err);
    setError("Failed to sign up with Google");
  } finally {
    setLoading(false);
  }
};

// Facebook 注册
const handleFacebookSignup = async () => {
  try {
    setError("");
    setLoading(true);
    await signUpWithFacebook();
    navigate("/");
  } catch (err) {
    console.error(err);
    setError("Failed to sign up with Facebook");
  } finally {
    setLoading(false);
  }
};

  // Microsoft 注册
  const handleMicrosoftSignup = async () => {
    try {
      setError("");
      setLoading(true);
      await signUpWithMicrosoft();
      navigate("/");
    } catch (err) {
      console.error(err);
      setError("Failed to sign up with Microsoft");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="form-container">
      {/* 使用 Card 组件构建注册表单容器 */}
      <Card>
        <Card.Body>
          {/* 页面标题 */}
          <h2 className="text-center mb-4">Sign Up</h2>
          {/* 如果存在错误信息，则显示 Alert 组件 */}

            {/* {currentUser && currentUser.email} */}
            {/* 在页面上显示当前登陆的用户 */}

          {error && <Alert variant="danger">{error}</Alert>}
          {/* 注册表单 */}
          <Form onSubmit={handleSubmit}>
            {/* 邮箱输入组 */}
            <Form.Group id="email" className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                ref={emailRef}
                required
                placeholder="Enter your email"
              />
            </Form.Group>
            {/* 密码输入组 */}
            <Form.Group id="password" className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                ref={passwordRef}
                required
                placeholder="Enter your password"
              />
            </Form.Group>
            {/* 密码确认输入组 */}
            <Form.Group id="password-confirm" className="mb-3">
              <Form.Label>Password Confirmation</Form.Label>
              <Form.Control
                type="password"
                ref={passwordConfirmRef}
                required
                placeholder="Confirm your password"
              />
            </Form.Group>
            {/* 提交按钮，根据 loading 状态动态显示文本，并在加载时禁用按钮 */}
            <Button disabled={loading} className="w-100" type="submit">
              {loading ? "Signing Up..." : "Sign Up"}
            </Button>
          </Form>
          <hr />

          <div className="text-center">
            <p>Or sign up with:</p>
            <Button  className="w-100 mb-2" onClick={handleGoogleSignup}>
              Sign Up with Google
            </Button>
            <Button  className="w-100 mb-2" onClick={handleFacebookSignup}>
              Sign Up with Facebook
            </Button>
            <Button className="w-100" onClick={handleMicrosoftSignup}>
              Sign Up with Microsoft
            </Button>
          </div>

        </Card.Body>
      </Card>
      {/* 页面底部导航链接 */}
      <div className="w-100 text-center mt-2">
        {/* 提供已拥有账户的用户跳转到登录页面的链接 */}
        Already have an account? <Link to="/login">Log In</Link>
        <br />
        {/* 提供返回首页的链接 */}
        <Link to="/">Go to Homepage</Link>
      </div>
    </div>
  );
};

export default Signup;